import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { siteInfo } from "@/mocks/siteData";
import { usePageSEO } from "@/hooks/usePageSEO";

const SITE_URL = typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL
  ? import.meta.env.VITE_SITE_URL.replace(/\/+$/, '')
  : 'https://superbuyspreadsheet.bar';

export default function SuperbuySpreadsheetGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  usePageSEO({
    title: "The Complete Superbuy Spreadsheet Guide 2026 | How to Buy Smart",
    description: "Everything about using the Superbuy Spreadsheet — find trusted sellers, compare products, and shop Chinese marketplaces confidently. Step-by-step guide with pro tips.",
    keywords: "superbuy spreadsheet guide, superbuy spreadsheet tutorial, how to use superbuy spreadsheet, superbuy spreadsheet beginners guide",
    canonicalPath: "/article/superbuy-spreadsheet-guide",
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "The Complete Superbuy Spreadsheet Guide: How to Find Trusted Sellers and Buy Smart in 2026",
      "description": "Everything you need to know about using the Superbuy Spreadsheet to discover verified product links, compare sellers, and shop confidently from Chinese marketplaces.",
      "datePublished": "2026-06-15",
      "dateModified": "2026-06-21",
      "author": { "@type": "Organization", "name": "Superbuy Spreadsheet Editorial Team" },
      "publisher": { "@type": "Organization", "name": "Superbuy Spreadsheet", "url": SITE_URL },
      "url": `${SITE_URL}/article/superbuy-spreadsheet-guide`,
    },
  });

  return (
    <>
      <Navbar />
      <main>
        <article className="bg-background-50 pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="text-xs text-foreground-400 mb-8 flex items-center gap-2 flex-wrap">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span className="text-foreground-600">Superbuy Spreadsheet Complete Guide</span>
            </nav>

            <header className="mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">Beginners Guide</span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-[1.15] mb-4">
                The Complete Superbuy Spreadsheet Guide: How to Find Trusted Sellers and Buy Smart in 2026
              </h1>
              <p className="text-base text-foreground-500 leading-relaxed">
                Everything you need to know about using the Superbuy Spreadsheet to discover verified product links, compare sellers, and shop confidently from Chinese marketplaces.
              </p>
              <div className="flex items-center gap-3 mt-6 pt-6 border-t border-foreground-100">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-user-line text-accent-600 w-4 h-4 flex items-center justify-center"></i>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">Editorial Team</div>
                  <div className="text-xs text-foreground-500">Updated June 2026 &middot; 8 min read</div>
                </div>
              </div>
            </header>

            <div className="space-y-8 text-foreground-700">
              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  What Is the Superbuy Spreadsheet?
                </h2>
                <p className="text-base leading-relaxed mb-4">
                  The <strong>Superbuy Spreadsheet</strong> is a carefully curated collection of product links designed for international buyers who want to shop from Chinese marketplaces through agents like Superbuy. Instead of spending hours browsing forums, Reddit threads, and Discord servers to find trustworthy sellers, the Superbuy Spreadsheet puts everything in one organized, easy-to-navigate document.
                </p>
                <p className="text-base leading-relaxed mb-4">
                  Think of it as your personal shopping directory. The spreadsheet organizes thousands of verified product listings across categories like sneakers, hoodies, jackets, bags, watches, and accessories. Each entry includes the product link, seller name, approximate pricing, size availability, and quality notes — all updated regularly by a community of experienced buyers.
                </p>
                <p className="text-base leading-relaxed">
                  For anyone new to buying from China, the <strong>Superbuy Spreadsheet</strong> is especially valuable because it removes the guesswork. You no longer need to wonder whether a seller is reliable or whether a product will match its photos. The spreadsheet only includes links that have been tested and verified by real buyers who have already gone through the ordering and quality control process.
                </p>
              </section>

              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Why Use the Superbuy Spreadsheet in 2026?
                </h2>
                <p className="text-base leading-relaxed mb-4">
                  The landscape of buying from Chinese marketplaces has changed dramatically over the past few years. With more sellers entering the market every month, finding genuinely good products has become harder, not easier. The <strong>Superbuy Spreadsheet</strong> solves this problem by doing the hard work of filtering and verifying for you.
                </p>
                <div className="overflow-x-auto my-6 rounded-xl border border-foreground-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-5 py-3 font-semibold text-foreground-900">Feature</th>
                        <th className="text-left px-5 py-3 font-semibold text-foreground-900">Superbuy Spreadsheet</th>
                        <th className="text-left px-5 py-3 font-semibold text-foreground-900">Traditional Search</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-foreground-100">
                        <td className="px-5 py-3 text-foreground-700">Speed</td>
                        <td className="px-5 py-3 text-foreground-900 font-medium">Instant access to verified links</td>
                        <td className="px-5 py-3 text-foreground-500">Hours of forum browsing</td>
                      </tr>
                      <tr className="border-t border-foreground-100">
                        <td className="px-5 py-3 text-foreground-700">Seller Access</td>
                        <td className="px-5 py-3 text-foreground-900 font-medium">50+ trusted sellers listed</td>
                        <td className="px-5 py-3 text-foreground-500">Limited to known names</td>
                      </tr>
                      <tr className="border-t border-foreground-100">
                        <td className="px-5 py-3 text-foreground-700">Product Variety</td>
                        <td className="px-5 py-3 text-foreground-900 font-medium">10K+ organized across categories</td>
                        <td className="px-5 py-3 text-foreground-500">Scattered across platforms</td>
                      </tr>
                      <tr className="border-t border-foreground-100">
                        <td className="px-5 py-3 text-foreground-700">Price Comparison</td>
                        <td className="px-5 py-3 text-foreground-900 font-medium">Side-by-side seller prices</td>
                        <td className="px-5 py-3 text-foreground-500">Manual tab switching</td>
                      </tr>
                      <tr className="border-t border-foreground-100">
                        <td className="px-5 py-3 text-foreground-700">Updates</td>
                        <td className="px-5 py-3 text-foreground-900 font-medium">Regularly refreshed</td>
                        <td className="px-5 py-3 text-foreground-500">Often outdated</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  How to Get Started with the Superbuy Spreadsheet
                </h2>
                <p className="text-base leading-relaxed mb-4">
                  Getting started with the <strong>Superbuy Spreadsheet</strong> is straightforward, even if you have never used a shopping agent before. Here is a step-by-step walkthrough:
                </p>
                <ol className="list-decimal pl-5 space-y-4 text-base leading-relaxed">
                  <li>
                    <strong className="text-foreground-900">Access the spreadsheet</strong> — Navigate to the main platform and open the Superbuy Spreadsheet. You will see it organized by categories like Shoes, Hoodies, T-Shirts, Jackets, and more.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Browse by category</strong> — Select the category that interests you. Each section contains product links, seller information, and quality notes submitted by the community.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Copy a product link</strong> — Find an item you like and copy its link. The spreadsheet often includes multiple seller options for popular items so you can compare.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Paste into Superbuy</strong> — Open your Superbuy account and paste the product link into the search bar. The agent will recognize the listing and let you proceed with the order.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Review QC photos</strong> — When your item arrives at the warehouse, check the quality control photos carefully. The spreadsheet often includes QC tips for specific products.
                  </li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Is the Superbuy Spreadsheet Safe to Use?
                </h2>
                <p className="text-base leading-relaxed mb-4">
                  Safety is one of the most common questions from new users, and it is an important one. The <strong>Superbuy Spreadsheet</strong> itself is an informational resource — it does not sell products directly, and it does not collect payment information. Here is what you should know:
                </p>
                <ul className="list-disc pl-5 space-y-3 text-base leading-relaxed">
                  <li>
                    <strong className="text-foreground-900">Verified sellers only</strong> — Every link in the spreadsheet has been tested by community members. Sellers with poor reputations are removed from the list.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Agent protection</strong> — When you buy through Superbuy, the agent provides buyer protection, quality inspection, and secure payment processing.
                  </li>
                  <li>
                    <strong className="text-foreground-900">No personal data collected</strong> — The spreadsheet is a read-only resource. It does not require you to create an account or share any personal information.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Shipping insurance available</strong> — Superbuy offers shipping insurance options to protect your packages during international transit.
                  </li>
                </ul>
                <p className="text-base leading-relaxed mt-4">
                  As with any online purchase, we recommend doing your own research before placing an order. Check seller ratings, read QC experiences from other buyers, and start with smaller orders when trying a new seller for the first time.
                </p>
              </section>

              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  What Products Can You Find in the Superbuy Spreadsheet?
                </h2>
                <p className="text-base leading-relaxed mb-4">
                  The <strong>Superbuy Spreadsheet</strong> covers a remarkably wide range of product categories. Here is a brief overview of what you can expect:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <h3 className="font-semibold text-foreground-900 mb-2">Sneakers &amp; Footwear</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">The most popular category. Find everything from retro basketball shoes to limited-edition collaborations, organized by brand and model.</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <h3 className="font-semibold text-foreground-900 mb-2">Streetwear &amp; Clothing</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">Hoodies, t-shirts, jackets, pants, and full sets from popular streetwear brands. Size charts and fit notes are often included.</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <h3 className="font-semibold text-foreground-900 mb-2">Bags &amp; Accessories</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">Backpacks, shoulder bags, wallets, belts, sunglasses, and more. Great for finding accessories that complete your look.</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <h3 className="font-semibold text-foreground-900 mb-2">Jerseys &amp; Sportswear</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">Football, basketball, and other sports jerseys from various leagues. Includes sizing recommendations and season availability.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Pro Tips for Using the Superbuy Spreadsheet Effectively
                </h2>
                <p className="text-base leading-relaxed mb-4">
                  After helping thousands of buyers navigate the <strong>Superbuy Spreadsheet</strong>, here are some pro-level tips to get the most out of it:
                </p>
                <div className="space-y-4">
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <span className="text-xs font-bold text-accent-600 uppercase tracking-wider">Tip 1</span>
                    <p className="text-sm text-foreground-700 mt-1">Always check the last-updated date on a listing. Sellers sometimes change their product pages, so recent links are more reliable than older ones.</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <span className="text-xs font-bold text-accent-600 uppercase tracking-wider">Tip 2</span>
                    <p className="text-sm text-foreground-700 mt-1">Compare prices between sellers for the same product. The spreadsheet often lists multiple options, and prices can vary significantly even for identical items.</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <span className="text-xs font-bold text-accent-600 uppercase tracking-wider">Tip 3</span>
                    <p className="text-sm text-foreground-700 mt-1">Read the QC notes before ordering. Community members often leave feedback about which sizes run big, which colors look different in person, and which sellers provide the fastest shipping.</p>
                  </div>
                  <div className="p-5 rounded-xl bg-background-100 border border-background-200">
                    <span className="text-xs font-bold text-accent-600 uppercase tracking-wider">Tip 4</span>
                    <p className="text-sm text-foreground-700 mt-1">Build a haul list before placing orders. Grouping multiple items into a single shipment can save significantly on international shipping costs.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-5">
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">Do I need a Superbuy account to use the spreadsheet?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">You can browse the <strong>Superbuy Spreadsheet</strong> without an account. However, to actually place orders, you will need a Superbuy account to use their agent service.</p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">How often is the spreadsheet updated?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">The spreadsheet receives regular updates. New links are added weekly, and broken or outdated links are removed as the community reports them.</p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">Can I trust the seller ratings in the spreadsheet?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">The ratings come from real buyer experiences. However, always perform your own due diligence — check recent buyer reviews and QC photos from other customers before placing large orders.</p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">What if a product link is broken?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">Broken links happen occasionally. The spreadsheet often includes alternative links for popular products. You can also search for the seller name directly on the platform.</p>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">Is the Superbuy Spreadsheet free?</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">Yes, the spreadsheet is a free community resource. There is no charge to access the product links or buyer guides.</p>
                  </div>
                </div>
              </section>

              <section className="pt-4">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  Final Thoughts: Is the Superbuy Spreadsheet Worth It?
                </h2>
                <p className="text-base leading-relaxed mb-4">
                  For anyone serious about buying products from Chinese marketplaces, the <strong>Superbuy Spreadsheet</strong> is an invaluable tool. It saves you the most precious resource — time — by organizing verified links, seller ratings, and buyer tips all in one place. Whether you are buying your first pair of sneakers or restocking your reseller inventory, this spreadsheet gives you a significant advantage.
                </p>
                <p className="text-base leading-relaxed mb-4">
                  The combination of category organization, community-verified links, and regular updates makes the Superbuy Spreadsheet the most efficient way to shop through Superbuy. Instead of jumping between a dozen different sources trying to piece together your shopping list, you can find everything you need in one organized location.
                </p>
                <p className="text-base leading-relaxed">
                  Ready to start exploring? Head over to the <Link to="/" className="text-accent-600 font-semibold hover:text-accent-700 underline cursor-pointer">main page</Link> to browse categories and discover what the <strong>Superbuy Spreadsheet</strong> has to offer. For the full shopping experience, visit the main platform to access the complete spreadsheet and start building your next haul.
                </p>
              </section>
            </div>

            <div className="mt-12 p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200 text-center">
              <h3 className="text-xl font-heading font-bold text-foreground-950 mb-3">
                Ready to Explore More Products?
              </h3>
              <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                Browse our latest Superbuy Spreadsheet collections and discover trusted product links across all categories.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={siteInfo.mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Explore Now
                  <i className="ri-arrow-right-line ml-2 w-4 h-4 inline-flex items-center justify-center"></i>
                </a>
                <Link
                  to="/"
                  className="w-full sm:w-auto px-7 py-3 rounded-full border-2 border-foreground-800 text-foreground-800 text-sm font-semibold hover:bg-foreground-800 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Back to Home
                </Link>
              </div>
            </div>

            <div className="mt-10 pt-6 border-t border-foreground-100">
              <p className="text-xs text-foreground-400 leading-relaxed">
                <strong>Disclaimer:</strong> This website is an informational resource. We do not sell products directly. Users should perform their own research before purchasing products from third-party sellers. All trademarks belong to their respective owners. External links are provided for convenience and we may earn a small commission at no extra cost to you.
              </p>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}